/* eslint-disable eqeqeq */
import axios, { AxiosError } from 'axios';
import { Logger } from 'emrn-hybrid/Logger';
import { Image, Platform } from 'react-native';
import { HttpCancelHandle, CancelHandleOption } from '../HttpCancelHandle';

const TimeoutSymbol = `get-image-size-timeout-${Math.random()}`;
const CancelSymbol = `cancel-get-image-size-request-${Math.random()}`;

/**
 * 创建远程图片请求
 * @param url 地址
 * @param options 配置
 * @returns
 */
export const createRemoteImageRequest = async (url: string, options: CreateRemoteImageRequestOptions = {}) => {
  let requestError: any;
  const beginTime = Date.now();
  const { timeout = 20000 } = options;
  const result: RemoteImageRequestResult = {
    status: RemoteImageRequestStatus.UnkownError,
  };

  await getImageSize(url, timeout, options.cancelHandle)
    .then(metadata => {
      result.metadata = metadata;
      result.status = RemoteImageRequestStatus.Ok;
    })
    .catch((error: any) => {
      requestError = error;
      result.status = RemoteImageRequestStatus.UnkownError;
    });

  if (result.status != RemoteImageRequestStatus.Ok) {
    if (requestError == TimeoutSymbol) {
      result.status = RemoteImageRequestStatus.RequestTimeout;
    } else if (requestError == CancelSymbol) {
      result.status = RemoteImageRequestStatus.RequestCanceled;
    } else {
      if (Platform.OS == 'ios' && typeof requestError == 'object') {
        // 解析iOS端 Image.getSize方法失败回调error对象
        const code = (requestError.code || '' + '').toUpperCase();
        const domain = (requestError.domain || '' + '').toUpperCase();
        result.statusCode = +code.replace('E' + domain, '');
        isNaN(result.statusCode) && (result.statusCode = undefined);
      }
      if (result.statusCode === undefined && timeout - (Date.now() - beginTime) > 1000) {
        const cost = Date.now() - beginTime;
        await axios
          .get(url, {
            timeout: timeout - cost,
            headers: { 'Content-Type': 'image/png,image/jpeg' },
          })
          .then(response => {
            result.statusCode = response.status;
            result.statusText = response.statusText;
          })
          .catch((error: AxiosError) => {
            if (error.response) {
              result.statusCode = error.response.status;
              result.statusText = error.response.statusText;
            } else {
              result.statusText = error.message;
            }
          });
      }
    }

    if (result.statusCode !== undefined) {
      if (result.statusCode == 404) {
        result.status = RemoteImageRequestStatus.RequestNotFound;
      } else if (result.statusCode >= 500) {
        result.status = RemoteImageRequestStatus.ServerError;
      }
    }
  }

  if (result.status == RemoteImageRequestStatus.Ok) {
    result.message = '成功';
  } else if (result.status == RemoteImageRequestStatus.RequestTimeout) {
    result.message = '请求已超时，请稍后重试！';
  } else if (result.status == RemoteImageRequestStatus.ServerError) {
    result.message = '服务繁忙，请稍后重试';
  } else {
    result.message = '网络繁忙，请稍后再试！';
  }

  try {
    const logs: string[] = ['获取图片元数据结束'];
    if (!result.metadata || result.status != RemoteImageRequestStatus.Ok) {
      logs.push(`url=${url}`);
    }
    logs.push(`耗时=${Date.now() - beginTime}毫秒`);
    logs.push(`结果=${JSON.stringify(result)}`);
    // console.info(logs.join(", "));
    Logger.info({
      tag: '邀新活动',
      msg: logs.join(', '),
    });
  } catch (error) {
    //console.warn(error);
  }

  // 取消请求时，不再返回结果
  if (result.status == RemoteImageRequestStatus.RequestCanceled) {
    throw new Error(result.message);
  }

  return result;
};

function getImageSize(uri: string, timeout: number, cancelHandle?: HttpCancelHandle) {
  return new Promise<RemoteImageMetadata>((resolve, reject) => {
    let canceled = false;
    let timeouted = false;

    const timer = window.setTimeout(() => {
      timeouted = true;
      reject(TimeoutSymbol);
    }, Math.max(1000, timeout));

    if (cancelHandle) {
      cancelHandle.cancel = () => {
        canceled = true;
        window.clearTimeout(timer);
        reject(CancelSymbol);
      };
    }

    Image.getSize(
      uri,
      (width: number, height: number) => {
        if (!canceled && !timeouted) {
          window.clearTimeout(timer);
          resolve({ width, height, uri });
        }
      },
      (error: any) => {
        if (!canceled && !timeouted) {
          window.clearTimeout(timer);
          reject(error);
        }
      }
    );
  });
}

/**
 * 远程图片元数据
 */
export interface RemoteImageMetadata {
  /**
   * 图片地址
   */
  uri: string;
  /**
   * 宽度
   */
  width: number;
  /**
   * 高度
   */
  height: number;
}

/**
 * 远程图片请求状态枚举
 */
export enum RemoteImageRequestStatus {
  /**
   * 正常
   */
  Ok = 'ok',
  /**
   * 未知错误
   */
  UnkownError = 'UnkownError',
  /**
   * 500+
   */
  ServerError = 'ServerError',
  /**
   * 网络错误
   */
  NetworkError = 'NetworkError',
  /**
   * 请求超时
   */
  RequestTimeout = 'RequestTimeout',
  /**
   * 404
   */
  RequestNotFound = 'RequestNotFound',
  /**
   * 请求被取消
   */
  RequestCanceled = 'RequestCanceled',
}

/**
 * 远程图片请求方法结果类型
 */
export interface RemoteImageRequestResult {
  /**
   * 状态
   */
  status: RemoteImageRequestStatus;
  /**
   * http状态码
   */
  statusCode?: number;
  /**
   * http状态语
   */
  statusText?: string;
  /**
   * 消息
   */
  message?: string;
  /**
   * 元数据
   */
  metadata?: RemoteImageMetadata;
}

/**
 * 创建远程图片请求方法配置
 */
export interface CreateRemoteImageRequestOptions extends CancelHandleOption {
  /**
   * 超时时间
   * 1. 默认`20秒`
   * 2. 最小`1秒`
   */
  timeout?: number;
}
